import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cloneRole } from '@/api/services/roles.service'
import { Button, Input, Modal } from '@/components/ui'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type { Role } from '@/types'
import { rolesQueryKeys } from '../hooks/useRoles'

function CloneRoleForm({ role, onClose }: { role: Role; onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient()
  const [name, setName] = useState(`${role.name} copy`)

  const clone = useMutation({
    mutationFn: () => cloneRole(role._id, name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.all })
      toast.success('Role cloned')
      onClose()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const canSubmit = name.trim().length > 0 && !clone.isPending

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (canSubmit) clone.mutate()
      }}
    >
      <Input
        id="clone-role-name"
        label="New role name"
        value={name}
        maxLength={100}
        autoFocus
        onChange={(event) => setName(event.target.value)}
      />
      <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
        Starts with the same permissions, level, and scope. Nobody is moved to the new role.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <Button type="button" variant="secondary" className="whitespace-nowrap" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="whitespace-nowrap"
          disabled={!canSubmit}
          loading={clone.isPending}
        >
          Clone role
        </Button>
      </div>
    </form>
  )
}

export interface CloneRoleModalProps {
  /** The role to copy. The modal is closed while this is null. */
  role: Role | null
  onClose: () => void
}

export function CloneRoleModal({ role, onClose }: CloneRoleModalProps): React.ReactElement {
  return (
    <Modal
      open={role !== null}
      onClose={onClose}
      title={role ? `Clone ${role.name}` : ''}
      className="max-w-md"
    >
      {role ? <CloneRoleForm key={role._id} role={role} onClose={onClose} /> : null}
    </Modal>
  )
}

CloneRoleModal.displayName = 'CloneRoleModal'
